import React, { useRef, useState, useCallback } from 'react'
import { getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  onChange?: (file: File | null) => void
  onChangeMultiple?: (files: File[]) => void
  preview?: string | null
  accept?: string
  error?: boolean
  helperText?: string
  label?: string
  showPreview?: boolean
  maxSizeMB?: number
}

const FileInput: React.FC<FileInputProps> = ({
  onChange,
  onChangeMultiple,
  preview,
  accept = 'image/*',
  error = false,
  helperText,
  label,
  showPreview = true,
  maxSizeMB,
  disabled,
  className = '',
  id,
  multiple,
  ...props
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)

  const handleFileSelect = useCallback(
    (file: File | null) => {
      if (!file) {
        onChange?.(null)
        setFileError(null)
        return
      }

      // Validar tamanho do arquivo
      if (maxSizeMB) {
        const fileSizeMB = file.size / (1024 * 1024)
        if (fileSizeMB > maxSizeMB) {
          setFileError(`O arquivo é muito grande. Tamanho máximo: ${maxSizeMB}MB`)
          onChange?.(null)
          return
        }
      }

      setFileError(null)
      onChange?.(file)
    },
    [onChange, maxSizeMB]
  )

  const handleFilesSelect = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        onChangeMultiple?.([])
        setFileError(null)
        return
      }

      // Validar tamanho dos arquivos
      if (maxSizeMB) {
        const invalidFiles = files.filter(file => {
          const fileSizeMB = file.size / (1024 * 1024)
          return fileSizeMB > maxSizeMB
        })
        
        if (invalidFiles.length > 0) {
          setFileError(`Alguns arquivos são muito grandes. Tamanho máximo: ${maxSizeMB}MB`)
          onChangeMultiple?.([])
          return
        }
      }

      setFileError(null)
      onChangeMultiple?.(files)
    },
    [onChangeMultiple, maxSizeMB]
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (multiple && files) {
      const filesArray = Array.from(files)
      handleFilesSelect(filesArray)
    } else {
      const file = files?.[0] || null
      handleFileSelect(file)
    }
    // Resetar o input para permitir selecionar os mesmos arquivos novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const files = e.dataTransfer.files
    if (multiple && files) {
      const filesArray = Array.from(files)
      handleFilesSelect(filesArray)
    } else {
      const file = files?.[0] || null
      if (file) {
        handleFileSelect(file)
      }
    }
    // Resetar o input após processar o drop
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    handleFileSelect(null)
  }

  const displayError: boolean = !!(error || fileError)
  const displayHelperText = helperText || fileError

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className={getInputLabelClasses()}
        >
          {label}
        </label>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative
          w-full
          min-h-[120px]
          border-2
          border-dashed
          rounded-xl
          transition-all
          duration-200
          cursor-pointer
          mt-0
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}
          ${error || fileError ? 'border-red-300 bg-red-50 focus-within:border-red-500 focus-within:ring-4 focus-within:ring-red-500/10' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-blue-400 hover:bg-gray-100 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-500/10'}
          ${preview && showPreview ? 'p-4' : 'p-6'}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          id={id}
          accept={accept}
          onChange={handleInputChange}
          disabled={disabled}
          multiple={multiple}
          className="hidden"
          {...props}
        />

        {preview && showPreview ? (
          <div className="relative">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <img
                  src={preview}
                  alt="Preview"
                  className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  Imagem selecionada
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Clique para selecionar outra imagem
                </p>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="flex-shrink-0 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  aria-label="Remover arquivo"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-3">
              <svg
                className={`w-12 h-12 mx-auto ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {isDragging ? 'Solte o arquivo aqui' : 'Clique para selecionar ou arraste e solte'}
            </p>
            <p className="text-xs text-gray-500">
              {accept.includes('image') ? 'PNG, JPG, GIF ou WEBP' : 'Arquivo permitido'}
              {multiple && ' (múltiplos arquivos)'}
              {maxSizeMB && ` (máx. ${maxSizeMB}MB)`}
            </p>
          </div>
        )}
      </div>

      {displayHelperText && (
        <p className={getInputHelperTextClasses(!!displayError)}>
          {displayHelperText}
        </p>
      )}
    </div>
  )
}

FileInput.displayName = 'FileInput'

export default FileInput

